import { Screen } from '../ui/core/Screen';
import { EventBus, Events } from './EventBus';
import { StateManager } from './StateManager';
import { setPresence } from '../discord';
import type { PlayerData } from '../types';

/** Map screen IDs to Rich Presence display text */
const SCREEN_PRESENCE: Record<string, string> = {
  'main-menu': 'In Main Menu',
  'party': 'Managing Party',
  'crafting': 'In the Workshop',
  'crawls': 'Viewing Campaign',
  'shop': 'In the Shop',
  'soulloom': 'At the Soulloom',
  'combat': 'In Combat',
  'crawl-detail': 'Preparing for a Crawl',
  'hero-select': 'Selecting Party',
  'inspect': 'Inspecting a Player',
  'codex': 'Reading the Codex',
};

type ScreenFactory = () => Screen;

/**
 * Tab unlock schedule — campaignStep required to unlock each nav tab.
 * 0 = always, 1 = after Ch.1, etc.
 * Shop opens after Ch.3.
 * Loom unlocks deep in Act 3 (Ch.15).
 */
const TAB_UNLOCK_STEP: Record<string, number> = {
  'main-menu': 0,
  'party': 0,
  'crafting': 1,
  'crawls': 0,
  'shop': 3,
  'soulloom': 15,
  'codex': 0,
};

/** Human-readable hint for locked tabs */
function lockHint(requiredStep: number): string {
  if (requiredStep >= 10) return 'Act 3';
  if (requiredStep >= 5) return 'Act 2';
  return `Ch. ${requiredStep}`;
}

class ScreenRouterClass {
  private screens = new Map<string, Screen>();
  private factories = new Map<string, ScreenFactory>();
  private activeScreenId: string | null = null;
  private history: string[] = [];

  private screenContainer: HTMLElement | null = null;
  private battleContainer: HTMLElement | null = null;
  private navBar: HTMLElement | null = null;
  private transitioning = false;

  /** Call once after DOM ready */
  initialize(): void {
    this.screenContainer = document.getElementById('screen-container');
    this.battleContainer = document.getElementById('battle-container');
    this.navBar = document.getElementById('nav-bar');

    // Wire up nav-bar tab clicks
    this.navBar?.querySelectorAll('.nav-tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        const el = tab as HTMLElement;
        if (el.classList.contains('nav-tab-locked')) return;
        const screenId = el.dataset.screen;
        if (screenId) this.navigate(screenId);
      });
    });

    EventBus.on(Events.NAVIGATE, (id: unknown, params?: unknown) => {
      this.navigate(id as string, params as Record<string, unknown>);
    });
    EventBus.on(Events.NAVIGATE_BACK, () => this.back());

    // Re-evaluate tab locks when campaign progresses
    StateManager.subscribe('playerData', () => this.refreshTabLocks());
    this.refreshTabLocks();
  }

  /** Register a screen factory (lazy instantiation) */
  register(id: string, factory: ScreenFactory): void {
    this.factories.set(id, factory);
  }

  /** Register a pre-built screen instance (for combat, which already exists) */
  registerInstance(id: string, instance: Screen): void {
    this.screens.set(id, instance);
  }

  /** Navigate to a screen by ID */
  navigate(id: string, params?: Record<string, unknown>): void {
    if (id === this.activeScreenId) return;

    // Resolve target BEFORE leaving current screen
    let target = this.screens.get(id);
    if (!target) {
      const factory = this.factories.get(id);
      if (!factory) {
        console.error(`[ScreenRouter] Unknown screen: ${id}`);
        return;
      }
      target = factory();
      this.screens.set(id, target);

      // Non-Phaser screens create DOM in #screen-container
      if (!target.screenConfig.needsPhaser && this.screenContainer) {
        target.createDOM(this.screenContainer);
        target.initialize();
      }
    }

    const current = this.activeScreenId ? this.screens.get(this.activeScreenId) : null;

    // Push to history
    if (this.activeScreenId) {
      this.history.push(this.activeScreenId);
    }

    // Decide: fade transition for screen-to-screen, instant for combat/first-load
    const canFade = current
      && !current.screenConfig.needsPhaser
      && !target.screenConfig.needsPhaser
      && this.screenContainer
      && !this.transitioning;

    if (canFade) {
      this.transitionSwap(current!, target, id, params);
    } else {
      // Instant swap (first load, combat, or mid-transition)
      if (current) {
        current.onLeave();
        current.hide();
      }
      this.showContainer(target.screenConfig.needsPhaser);
      this.updateNavBar(id, target.screenConfig.needsPhaser, target.screenConfig.hideNavBar);
      target.show();
      target.onEnter(params);
      this.activeScreenId = id;
    }

    // Update Rich Presence
    const presenceText = SCREEN_PRESENCE[id];
    if (presenceText) {
      let state: string | undefined;
      if (id === 'combat' || id === 'crawl-detail' || id === 'hero-select') {
        const crawlData = StateManager.get('crawlData') as { config?: { name?: string } } | null;
        state = crawlData?.config?.name;
      }
      setPresence(presenceText, state);
    }
  }

  /** Go back to previous screen */
  back(): void {
    const prevId = this.history.pop();
    if (prevId) {
      const currentId = this.activeScreenId;
      this.navigate(prevId);
      // Remove the entry navigate() just pushed (avoid double-push)
      if (this.history[this.history.length - 1] === currentId) {
        this.history.pop();
      }
    }
  }

  /** Replace current screen without pushing to history */
  replace(id: string, params?: Record<string, unknown>): void {
    // Resolve target BEFORE leaving current screen
    let target = this.screens.get(id);
    if (!target) {
      const factory = this.factories.get(id);
      if (!factory) {
        console.error(`[ScreenRouter] Unknown screen: ${id}`);
        return;
      }
      target = factory();
      this.screens.set(id, target);
      if (!target.screenConfig.needsPhaser && this.screenContainer) {
        target.createDOM(this.screenContainer);
        target.initialize();
      }
    }

    const current = this.activeScreenId ? this.screens.get(this.activeScreenId) : null;

    const canFade = current
      && !current.screenConfig.needsPhaser
      && !target.screenConfig.needsPhaser
      && this.screenContainer
      && !this.transitioning;

    if (canFade) {
      this.transitionSwap(current!, target, id, params);
    } else {
      if (current) {
        current.onLeave();
        current.hide();
      }
      this.showContainer(target.screenConfig.needsPhaser);
      this.updateNavBar(id, target.screenConfig.needsPhaser, target.screenConfig.hideNavBar);
      target.show();
      target.onEnter(params);
      this.activeScreenId = id;
    }
  }

  /** Fade out container → swap screens → fade in.
   *  Returning to main-menu: fade out only, then snap visible (menu is "always there").
   *  Everything else: full fade out → swap → fade in. */
  private transitionSwap(
    current: Screen,
    target: Screen,
    id: string,
    params?: Record<string, unknown>,
  ): void {
    const container = this.screenContainer!;
    this.transitioning = true;

    // Fade out current screen
    container.style.opacity = '0';

    setTimeout(() => {
      current.onLeave();
      current.hide();

      this.showContainer(target.screenConfig.needsPhaser);
      this.updateNavBar(id, target.screenConfig.needsPhaser, target.screenConfig.hideNavBar);

      target.show();
      target.onEnter(params);
      this.activeScreenId = id;

      if (id === 'main-menu') {
        // Snap visible — menu was "always there," we just closed the overlay
        container.style.transition = 'none';
        container.style.opacity = '1';
        void container.offsetHeight;
        container.style.transition = '';
        this.transitioning = false;
      } else {
        // Fade in the new screen
        void container.offsetHeight;
        container.style.opacity = '1';
        setTimeout(() => { this.transitioning = false; }, 80);
      }
    }, 80);
  }

  getActiveScreenId(): string | null {
    return this.activeScreenId;
  }

  private showContainer(needsPhaser: boolean): void {
    if (needsPhaser) {
      // Use visibility instead of display:none for battle container —
      // display:none causes Phaser's ScaleManager to resize the WebGL canvas
      // to 0x0, which corrupts GPU texture state and makes sprites invisible
      // on the next crawl entry.
      if (this.battleContainer) {
        this.battleContainer.style.visibility = '';
        this.battleContainer.style.pointerEvents = '';
      }
      this.screenContainer?.classList.add('hidden');
    } else {
      if (this.battleContainer) {
        this.battleContainer.style.visibility = 'hidden';
        this.battleContainer.style.pointerEvents = 'none';
      }
      this.screenContainer?.classList.remove('hidden');
    }
  }

  /** Show/hide nav bar and highlight active tab */
  private updateNavBar(screenId: string, needsPhaser: boolean, hideNavBar?: boolean): void {
    if (!this.navBar) return;

    // Hide nav bar during combat or when the screen requests immersive mode
    if (needsPhaser || hideNavBar) {
      this.navBar.classList.add('hidden');
      return;
    }
    this.navBar.classList.remove('hidden');

    // Highlight the matching tab (sub-screens highlight their parent tab)
    const tabId = screenId === 'crawl-detail' ? 'crawls'
      : screenId === 'hero-select' ? 'crawls'
      : screenId === 'inspect' ? 'main-menu'
      : screenId;
    this.navBar.querySelectorAll('.nav-tab').forEach((tab) => {
      const isActive = (tab as HTMLElement).dataset.screen === tabId;
      tab.classList.toggle('active', isActive);
    });
  }

  /** Update locked/unlocked state of nav tabs based on campaign progress */
  refreshTabLocks(): void {
    if (!this.navBar) return;
    const player = StateManager.get('playerData') as PlayerData | undefined;
    const step = player?.campaignStep ?? 0;

    this.navBar.querySelectorAll('.nav-tab').forEach((tab) => {
      const el = tab as HTMLElement;
      const screenId = el.dataset.screen ?? '';
      const requiredStep = TAB_UNLOCK_STEP[screenId] ?? 0;
      const locked = step < requiredStep;

      el.classList.toggle('nav-tab-locked', locked);

      // Add or remove the lock hint label
      let hint = el.querySelector('.nav-tab-lock-hint') as HTMLElement | null;
      if (locked) {
        if (!hint) {
          hint = document.createElement('span');
          hint.className = 'nav-tab-lock-hint';
          el.appendChild(hint);
        }
        hint.textContent = lockHint(requiredStep);
      } else if (hint) {
        hint.remove();
      }
    });
  }
}

export const ScreenRouter = new ScreenRouterClass();
